import { useCallback, useState } from "react";
import { Sector } from "recharts";

/*
 * The one place that defines how a GRC donut looks and behaves.
 *
 * There are two donut *shells* by design — the dashboard card (paged click-to-toggle legend, elbow
 * leader-line labels, centred total) in pages/GRCDashboardNew/grcDashboardHelpers.tsx, and the chat
 * bubble (right-hand legend with per-row value and share) in
 * components/ComplianceAssistant/charts/RadialChart.tsx. What they must NOT differ on is colour,
 * hover and motion: the same organisation has to be the same colour and pop the same way whether you
 * meet it on a dashboard or in the AI companion. Those live here and are imported by both.
 *
 * The backend keeps its own copy of CONTRIB_COLORS in
 * grc-ai-companion/.../tools/dashboard/DashboardChartMapper.java, because it colours the chat donut's
 * rows server-side and cannot import TypeScript. If the palette below changes, change it there too.
 */

/**
 * Top-risk-contributor slice colour **by rank**, worst first — not by organisation identity, so a
 * contributor's colour follows its position in the sorted list rather than its name.
 */
export const CONTRIB_COLORS = ["#ef4444", "#f59e0b", "#f97316", "#8b5cf6"];

/** Every contributor past {@link CONTRIB_COLORS}. */
export const OTHERS_COLOR = "#64748b";

/** Slice colour for a contributor at `rank` (0-based, worst first). */
export function contributorColor(rank: number): string {
  return rank < CONTRIB_COLORS.length ? CONTRIB_COLORS[rank] : OTHERS_COLOR;
}

/** The swatch colour a hidden legend row shows instead of its slice colour. */
export const HIDDEN_SWATCH_COLOR = "#94a3b8";

export interface SliceToggle {
  hidden: ReadonlySet<string>;
  isHidden: (label: string) => boolean;
  toggle: (label: string) => void;
}

/**
 * Click-a-legend-row-to-hide-that-slice, shared by both donuts.
 *
 * `sliceCount` is what the never-hide-everything guard measures against: an empty donut is a state
 * the user cannot get out of by looking at it, so the last visible slice refuses to disappear.
 */
export function useSliceToggle(sliceCount: number): SliceToggle {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());

  const toggle = useCallback(
    (label: string) =>
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(label)) next.delete(label);
        else next.add(label);
        return next.size >= sliceCount ? prev : next; // never hide every segment
      }),
    [sliceCount],
  );

  const isHidden = useCallback((label: string) => hidden.has(label), [hidden]);

  return { hidden, isHidden, toggle };
}

/**
 * Zero the hidden rows' value instead of dropping them.
 *
 * This is the whole reason toggling a slice *animates*: recharts tweens a sector between its old and
 * new angle when a row it is already rendering changes value, but hard-redraws with no transition if
 * the row disappears from the array. Keep every row present and the donut sweeps shut; filter the
 * array and it snaps.
 */
export function applyHidden<T>(
  rows: readonly T[],
  hidden: ReadonlySet<string>,
  labelOf: (row: T) => string,
  valueKey: string,
): T[] {
  if (hidden.size === 0) return rows as T[];
  return rows.map((row) => (hidden.has(labelOf(row)) ? ({ ...row, [valueKey]: 0 } as T) : row));
}

/** Sweep duration. */
export const DONUT_ANIM_MS = 650;

/** Recharts would otherwise default to plain "ease". */
export const DONUT_ANIM_EASING = "ease-in-out" as const;

/**
 * Sweep from 12 o'clock, clockwise. Recharts defaults to `startAngle: 0, endAngle: 360`, which builds
 * counter-clockwise from 3 o'clock — the most visible way the chat donut used to differ from the card.
 */
export const DONUT_START_ANGLE = 90;
export const DONUT_END_ANGLE = -270;

/** How far the hovered slice pops out. */
export const DONUT_HOVER_POP_PX = 8;

/*
 * Leader-line geometry, expressed as multiples of the slice's own outerRadius rather than pixels.
 * The dashboard card and the chat donut are different sizes, so a fixed `+16` elbow that looks right
 * on one overshoots the other. Deriving from the radius recharts hands us means one renderer serves
 * both, and any donut added later, without retuning.
 */
const LEADER_STUB = 1.02; // where the line leaves the arc
const LEADER_ELBOW = 1.18; // where it turns horizontal
const LEADER_RUN = 0.2; // how far it then runs sideways
const LEADER_TEXT = 0.15; // font size as a share of the radius…
const LEADER_TEXT_MIN = 9; // …clamped so it stays legible when small
const LEADER_TEXT_MAX = 13; // …and doesn't balloon on a large card

/** Blank vertical space a label needs to clear its neighbour, as a multiple of its font size. */
const LEADER_GAP = 1.25;

const RADIAN = Math.PI / 180;

/** Font size for a leader label at the given radius — same clamp the renderer applies. */
function leaderFontSize(outerRadius: number): number {
  return Math.max(LEADER_TEXT_MIN, Math.min(LEADER_TEXT_MAX, outerRadius * LEADER_TEXT));
}

/** A resolved label position, plus the side its spacing was worked out on. */
export interface LeaderLabelSlot {
  y: number;
  /** `1` right of centre, `-1` left. */
  dir: 1 | -1;
}

/**
 * Where each slice's leader label should sit vertically, once labels that would land on top of one
 * another have been pushed apart.
 *
 * <h3>Why this exists</h3>
 * Recharts calls a label renderer once per slice and tells it nothing about the other slices, so
 * every label is placed from its own `midAngle` alone. Three slices worth ~1% each have midAngles a
 * fraction of a degree apart, which puts their labels within a pixel or two of the same `y` — they
 * overwrite each other and the reader gets a smear. No amount of per-slice cleverness fixes that;
 * the decision needs all the labels at once, which is what this does.
 *
 * <h3>What it deliberately does NOT do</h3>
 * A label already clear of its neighbour is returned exactly where it started. This declutters a
 * collision, it does not relayout the chart — a donut whose slices are well spread must look the
 * same after this as before it.
 *
 * Left and right are resolved independently: a label on one side can never collide with one on the
 * other, and treating them as one list would shove them around for no reason.
 *
 * Zero-value slices are skipped rather than placed. They render no label, so reserving a gap for
 * one would push their neighbours apart to make room for nothing.
 *
 * @param values       slice values in render order — pass the same rows the Pie receives, so a slice
 *                     hidden via {@link applyHidden} (zeroed) is correctly ignored
 * @param startAngle   the Pie's start/end angles
 * @param paddingAngle the Pie's `paddingAngle`, if it sets one. Recharts takes the padding out of
 *                     the sweep before distributing the rest by value, so ignoring a non-zero
 *                     padding walks every label progressively further from its slice. Defaults to
 *                     0, which is what the two donuts that predate this render with.
 * @returns a slot per slice index, or `null` for slices that get no label. `dir` is the side the
 *          spacing was worked out on, so the caller can tell when its own geometry disagrees.
 */
export function resolveLeaderLabelSlots(
  values: readonly number[],
  startAngle: number,
  endAngle: number,
  cx: number,
  cy: number,
  outerRadius: number,
  minGap: number,
  paddingAngle = 0,
): (LeaderLabelSlot | null)[] {
  const slots = new Array<LeaderLabelSlot | null>(values.length).fill(null);
  const total = values.reduce((sum, v) => sum + (v > 0 ? v : 0), 0);
  if (total <= 0) return slots;

  const fullSweep = endAngle - startAngle;
  const direction = Math.sign(fullSweep) || 1;
  const drawn = values.reduce((n, v) => n + (v > 0 ? 1 : 0), 0);
  // One gap between each pair of drawn slices. Clamped so a heavily padded chart cannot invert the
  // sweep and send the labels round backwards.
  const padTotal = Math.min(Math.abs(fullSweep), paddingAngle * Math.max(0, drawn - 1));
  const sweep = fullSweep - direction * padTotal;

  const right: (LeaderLabelSlot & { index: number })[] = [];
  const left: (LeaderLabelSlot & { index: number })[] = [];

  let cumulative = 0;
  let drawnBefore = 0;
  for (let i = 0; i < values.length; i++) {
    const value = values[i] > 0 ? values[i] : 0;
    if (value === 0) continue;
    const midAngle =
      startAngle
      + ((cumulative + value / 2) / total) * sweep
      + direction * paddingAngle * drawnBefore;
    cumulative += value;
    drawnBefore++;

    const y = cy + outerRadius * LEADER_ELBOW * Math.sin(-midAngle * RADIAN);
    const dir: 1 | -1 = Math.cos(-midAngle * RADIAN) >= 0 ? 1 : -1;
    (dir === 1 ? right : left).push({ index: i, y, dir });
  }

  // The plot's own vertical extent. Labels spread inside it; the reverse sweep below pulls a
  // cluster back rather than letting it walk off the canvas.
  const top = cy - outerRadius * LEADER_ELBOW;
  const bottom = cy + outerRadius * LEADER_ELBOW;

  for (const side of [right, left]) {
    side.sort((a, b) => a.y - b.y);

    // Forward: push a label down only when it would overlap the one above it.
    for (let i = 1; i < side.length; i++) {
      const gap = side[i].y - side[i - 1].y;
      if (gap < minGap) side[i].y = side[i - 1].y + minGap;
    }
    // Reverse: if that pushed the tail past the bottom, walk back up so the cluster spreads around
    // where it was instead of piling against the edge.
    if (side.length > 0 && side[side.length - 1].y > bottom) {
      side[side.length - 1].y = bottom;
      for (let i = side.length - 2; i >= 0; i--) {
        const gap = side[i + 1].y - side[i].y;
        if (gap < minGap) side[i].y = Math.max(top, side[i + 1].y - minGap);
      }
    }

    for (const entry of side) slots[entry.index] = { y: entry.y, dir: entry.dir };
  }

  return slots;
}

/**
 * Builds the elbow-leader-line label renderer for one donut, with its labels decluttered.
 *
 * A renderer has to be built per donut rather than shared as a plain function because decluttering
 * needs every slice's value, and recharts hands the renderer one slice at a time. Pass the rows the
 * Pie itself receives.
 *
 * Positions are resolved on the first call of a render pass — `cx`/`cy`/`outerRadius` only exist at
 * render time — and cached against that geometry so the remaining slices in the pass reuse the
 * answer instead of each recomputing the whole layout.
 *
 * Slices worth 0 get no label at all — an organisation with no findings should not have a `0.00%`
 * tag pointing at a sliver of nothing.
 */
export function makeLeaderLabelRenderer(
  values: readonly number[],
  startAngle: number,
  endAngle: number,
  paddingAngle = 0,
) {
  let cacheKey = "";
  let resolved: (LeaderLabelSlot | null)[] = [];

  // recharts types `label` so it rejects a narrowed param signature - `any` is required here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function LeaderLabel(props: any) {
    const { cx, cy, midAngle, outerRadius, percent, payload, index } = props;
    if (!percent) return null;

    const fontSize = leaderFontSize(outerRadius);

    const key = `${cx}|${cy}|${outerRadius}`;
    if (key !== cacheKey) {
      resolved = resolveLeaderLabelSlots(
        values, startAngle, endAngle, cx, cy, outerRadius, fontSize * LEADER_GAP, paddingAngle,
      );
      cacheKey = key;
    }

    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const sx = cx + outerRadius * LEADER_STUB * cos;
    const sy = cy + outerRadius * LEADER_STUB * sin;
    const mx = cx + outerRadius * LEADER_ELBOW * cos;
    const my = cy + outerRadius * LEADER_ELBOW * sin;
    const dir = cos >= 0 ? 1 : -1;
    const ex = mx + dir * outerRadius * LEADER_RUN;
    // Fall back to the slice's own y when the declutter has nothing for this index, or when it
    // spaced the label on the other side from the one this slice's own geometry runs to. The
    // second case is only reachable when the resolver's angles drift from recharts' — `minAngle`
    // grows a sliver and nudges its neighbours, and a slice sitting within a degree or two of 12
    // or 6 o'clock can land on the far side. Better it keeps its own position than be spaced
    // against a line pointing the other way.
    const slot = resolved[index];
    const ey = slot && slot.dir === dir ? slot.y : my;
    // The dashboard's rows carry `color`; the companion's normalised rows carry `fill`.
    const color = payload?.color ?? payload?.fill;

    return (
      <g>
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={color} strokeWidth={1.2} fill="none" />
        <circle cx={ex} cy={ey} r={1.8} fill={color} />
        <text
          x={ex + dir * 4}
          y={ey}
          textAnchor={cos >= 0 ? "start" : "end"}
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight={700}
          fill="var(--foreground)"
        >
          {(percent * 100).toFixed(2)}%
        </text>
      </g>
    );
  };
}

/**
 * How much room a leader label needs outside the arc, as a share of the plot's half-size.
 * Used to derive a chart margin instead of hardcoding one.
 *
 * <p>This is now needed vertically as well as horizontally: decluttering pushes a cluster of labels
 * up and down off their slices' own angles, so a caller that reserves only half of this above and
 * below will clip them.
 */
export const LEADER_LABEL_ROOM = LEADER_ELBOW - 1 + LEADER_RUN;

/*
 * Fitting a leader-labelled ring to its box.
 *
 * {@link LEADER_LABEL_ROOM} is the older, blunter answer: reserve a fixed share of the plot on all
 * four sides and let recharts size the ring in what is left. It has two faults. It measures the
 * leader LINE only, so the text written past the end of the line — "100.00%" runs about four em
 * further — crops against the plot edge. And it charges the same share to every side, so a card
 * wider than it is tall shrinks its ring to buy horizontal room it already had spare.
 *
 * What follows sizes the ring instead: the largest radius whose labels still fit the box, from
 * which the caller derives its margin. The inversion is not a subtraction because the label's font
 * scales with the radius, so the text's own width moves with what is being solved for.
 */

/** Em width of "100.00%" — the widest label a percentage can produce — in the label's own font. */
const LEADER_LABEL_EM = 4.1;

/** Gap between the leader's end dot and its text. Matches the renderer's `dir * 4`. */
const LEADER_TEXT_GAP = 4;

/** How far a label reaches from the ring's centre, horizontally and vertically, at radius `r`. */
function leaderLabelExtent(r: number): { x: number; y: number } {
  return {
    x: r * (LEADER_ELBOW + LEADER_RUN) + LEADER_TEXT_GAP + LEADER_LABEL_EM * leaderFontSize(r),
    // Only half a line sits below the label's own y, and the declutter never pushes a label past
    // the elbow ring itself — resolveLeaderLabelSlots clamps to exactly that.
    y: r * LEADER_ELBOW + leaderFontSize(r) / 2,
  };
}

/** Largest radius whose labels fit within `halfWidth` of the centre. Inverts `extent.x`. */
function radiusForHalfWidth(halfWidth: number): number {
  const geometry = LEADER_ELBOW + LEADER_RUN;
  const room = halfWidth - LEADER_TEXT_GAP;
  // Solve with the font free to scale, then re-solve against whichever clamp that lands outside —
  // inside the clamp the text is a fixed width and the radius is a plain division.
  const flexible = room / (geometry + LEADER_LABEL_EM * LEADER_TEXT);
  const font = flexible * LEADER_TEXT;
  if (font > LEADER_TEXT_MAX) return (room - LEADER_LABEL_EM * LEADER_TEXT_MAX) / geometry;
  if (font < LEADER_TEXT_MIN) return (room - LEADER_LABEL_EM * LEADER_TEXT_MIN) / geometry;
  return flexible;
}

/** Largest radius whose labels fit within `halfHeight` of the centre. Inverts `extent.y`. */
function radiusForHalfHeight(halfHeight: number): number {
  return (halfHeight - LEADER_TEXT_MAX / 2) / LEADER_ELBOW;
}

export interface LeaderPlotFit {
  /** Radius the ring resolves to, in px. */
  radius: number;
  /** Margin to hand recharts so its own sizing lands on {@link radius}. */
  marginX: number;
  marginY: number;
}

/**
 * Fit a leader-labelled ring to a plot box.
 *
 * Recharts derives the radius from the plot area left after the margin — `outerRadius` is a share
 * of `min(areaWidth, areaHeight) / 2` — so the margin is the only lever a caller has over how big
 * the ring comes out. This works the relationship backwards: take the largest radius whose labels
 * clear the box in BOTH directions, then return the margin that produces it.
 *
 * Each direction is solved on its own, which is the point: a chart card is rarely square, and the
 * ring should be limited by the tighter of the two rather than by both at once.
 *
 * @param outerRadiusRatio the `outerRadius` percentage the caller gives its `<Pie>`, as a fraction
 */
export function fitLeaderLabelPlot(
  width: number,
  height: number,
  outerRadiusRatio: number,
): LeaderPlotFit {
  const limit = Math.min(radiusForHalfWidth(width / 2), radiusForHalfHeight(height / 2));
  // A box too small to label at all still gets a ring: cropped labels beat no chart.
  const radius = Math.max(limit, (Math.min(width, height) / 2) * 0.35);
  const areaHalf = radius / outerRadiusRatio;
  // Rounded UP, not to nearest: a margin half a pixel short hands recharts a slightly wider plot
  // area, which grows the radius past what was solved for and puts the label back over the edge.
  return {
    radius,
    marginX: Math.max(0, Math.ceil(width / 2 - areaHalf)),
    marginY: Math.max(0, Math.ceil(height / 2 - areaHalf)),
  };
}

/**
 * The widest a leader-labelled plot box is worth making at this height: the ring the height alone
 * allows, plus the room its labels need beside it. Past this the extra width is dead space, since
 * the radius is already capped vertically.
 *
 * Callers that cap their plot box for layout reasons want this as the cap when leader labels are
 * on — a cap that fits the ring but not its labels is what makes the ring shrink on a wide card.
 */
export function leaderPlotIdealWidth(height: number): number {
  return Math.round(2 * leaderLabelExtent(radiusForHalfHeight(height / 2)).x);
}

/**
 * The hovered slice pops outward with a soft glow **in its own colour** — there is no separate hover
 * colour. A contributor "turns orange" on hover only because orange is already its rank's fill.
 *
 * Pair this with an explicit `activeIndex` of `-1` for "nothing hovered". Passing `undefined` instead
 * lets recharts fall back to its own tooltip-driven active state, which leaves the glow stuck on a
 * slice after the cursor has left.
 */
// recharts types activeShape so it rejects a narrowed param signature - `any` is required here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function renderActiveShape(props: any) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g style={{ filter: `drop-shadow(0 0 6px ${fill}99)` }}>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + DONUT_HOVER_POP_PX}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
}
